/**
 * Campaign Service
 *
 * API service for campaign CRUD operations and contact selections.
 * Connects to FastAPI /api/campaigns endpoints.
 */

import { apiClient, handleApiError } from '@shared/services/apiClient';
import { apiCall } from '@shared/utils/apiWrapper';
import type {
  CampaignListResponse,
  CampaignResponse,
  CampaignDetail,
  CampaignCreateRequest,
  CampaignUpdateRequest,
  CampaignListParams,
  AddOrganizationsRequest,
  AddOrganizationsResponse,
  UpdateSelectionsRequest,
  SuccessResponse,
  CampaignLabel,
  BulkUpdateRequest,
  BulkUpdateResponse,
  ContactItem,
} from '@shared/types/api';

/**
 * Fetch list of campaigns
 *
 * @param params - Query parameters (status filter)
 * @returns Promise resolving to campaign list
 *
 * @example
 * ```typescript
 * const result = await fetchCampaigns({ status: 'active' });
 * console.log(result.campaigns.length);
 * ```
 */
export async function fetchCampaigns(
  params: CampaignListParams = {}
): Promise<CampaignListResponse> {
  const endpoint = params.status === 'active' ? '/campaigns/active' :
                   params.status === 'archived' ? '/campaigns/archived' :
                   '/campaigns';

  return apiCall('fetch campaigns', () =>
    apiClient.get<CampaignListResponse>(endpoint)
  );
}

/**
 * Fetch active campaigns only
 *
 * @returns Promise resolving to active campaign list
 *
 * @example
 * ```typescript
 * const activeCampaigns = await fetchActiveCampaigns();
 * ```
 */
export async function fetchActiveCampaigns(): Promise<CampaignListResponse> {
  return fetchCampaigns({ status: 'active' });
}

/**
 * Fetch archived campaigns only
 *
 * @returns Promise resolving to archived campaign list
 *
 * @example
 * ```typescript
 * const archivedCampaigns = await fetchArchivedCampaigns();
 * ```
 */
export async function fetchArchivedCampaigns(): Promise<CampaignListResponse> {
  return fetchCampaigns({ status: 'archived' });
}

/**
 * Fetch single campaign by ID
 *
 * @param campaignId - Campaign ID
 * @returns Promise resolving to campaign details
 *
 * @example
 * ```typescript
 * const campaign = await fetchCampaignById('CAMPAIGN_20251028_121851');
 * console.log(campaign.campaign_name);
 * ```
 */
export async function fetchCampaignById(campaignId: string): Promise<CampaignResponse> {
  return apiCall(`fetch campaign ${campaignId}`, () =>
    apiClient.get<CampaignResponse>(`/campaigns/${campaignId}`)
  );
}

/**
 * Fetch campaign with full organization and contact details
 *
 * @param campaignId - Campaign ID
 * @returns Promise resolving to campaign detail with organizations
 *
 * @example
 * ```typescript
 * const detail = await fetchCampaignDetail('CAMPAIGN_20251028_121851');
 * console.log(detail.total_organizations); // Number of organizations
 * ```
 */
export async function fetchCampaignDetail(campaignId: string): Promise<CampaignDetail> {
  // Note: The API endpoint is /campaigns/{id} not /campaigns/{id}/detail
  // The GET /campaigns/{id} endpoint returns CampaignDetail with full organizations array
  return apiCall(`fetch campaign detail ${campaignId}`, () =>
    apiClient.get<CampaignDetail>(`/campaigns/${campaignId}`)
  );
}

/**
 * Create new campaign
 *
 * @param data - Campaign creation data
 * @returns Promise resolving to created campaign
 *
 * @example
 * ```typescript
 * const newCampaign = await createCampaign({
 *   name: 'Q4 2025 Campaign',
 *   route_config: {
 *     start_location: 'Houston, TX',
 *     end_location: 'Houston, TX',
 *     weeks: 12,
 *     days_per_week: 5
 *   }
 * });
 * ```
 */
export async function createCampaign(data: CampaignCreateRequest): Promise<CampaignResponse> {
  return apiCall('create campaign', () =>
    apiClient.post<CampaignResponse>('/campaigns', data)
  );
}

/**
 * Update existing campaign
 *
 * @param campaignId - Campaign ID
 * @param data - Campaign update data (partial)
 * @returns Promise resolving to updated campaign
 *
 * @example
 * ```typescript
 * const updated = await updateCampaign('CAMPAIGN_20251028_121851', {
 *   name: 'Updated Campaign Name',
 *   status: 'active'
 * });
 * ```
 */
export async function updateCampaign(
  campaignId: string,
  data: CampaignUpdateRequest
): Promise<CampaignResponse> {
  return apiCall(`update campaign ${campaignId}`, () =>
    apiClient.patch<CampaignResponse>(`/campaigns/${campaignId}`, data)
  );
}

/**
 * Delete campaign
 *
 * @param campaignId - Campaign ID
 * @returns Promise resolving to success message
 *
 * @example
 * ```typescript
 * await deleteCampaign('CAMPAIGN_20251028_121851');
 * console.log('Campaign deleted successfully');
 * ```
 */
export async function deleteCampaign(campaignId: string): Promise<SuccessResponse> {
  return apiCall(`delete campaign ${campaignId}`, () =>
    apiClient.delete<SuccessResponse>(`/campaigns/${campaignId}`)
  );
}

/**
 * Archive campaign
 *
 * @param campaignId - Campaign ID
 * @returns Promise resolving to updated campaign
 *
 * @example
 * ```typescript
 * await archiveCampaign('CAMPAIGN_20251028_121851');
 * console.log('Campaign archived successfully');
 * ```
 */
export async function archiveCampaign(campaignId: string): Promise<CampaignResponse> {
  return apiCall(`archive campaign ${campaignId}`, () =>
    apiClient.post<CampaignResponse>(`/campaigns/${campaignId}/archive`)
  );
}

/**
 * Unarchive campaign
 *
 * @param campaignId - Campaign ID
 * @returns Promise resolving to updated campaign
 *
 * @example
 * ```typescript
 * await unarchiveCampaign('CAMPAIGN_20251028_121851');
 * console.log('Campaign unarchived successfully');
 * ```
 */
export async function unarchiveCampaign(campaignId: string): Promise<CampaignResponse> {
  return apiCall(`unarchive campaign ${campaignId}`, () =>
    apiClient.post<CampaignResponse>(`/campaigns/${campaignId}/unarchive`)
  );
}

/**
 * Add organizations to campaign
 *
 * @param campaignId - Campaign ID
 * @param data - Organization IDs to add
 * @returns Promise resolving to add response with count
 *
 * @example
 * ```typescript
 * const result = await addOrganizationsToCampaign('CAMPAIGN_20251028_121851', {
 *   organization_ids: [123, 456, 789]
 * });
 * console.log(`Added ${result.added_count} organizations`);
 * ```
 */
export async function addOrganizationsToCampaign(
  campaignId: string,
  data: AddOrganizationsRequest
): Promise<AddOrganizationsResponse> {
  return apiCall(`add organizations to campaign ${campaignId}`, () =>
    apiClient.post<AddOrganizationsResponse>(
      `/campaigns/${campaignId}/organizations`,
      data
    )
  );
}

/**
 * Remove organizations from campaign
 *
 * @param campaignId - Campaign ID
 * @param orgIds - Organization IDs to remove
 * @returns Promise resolving to success message
 *
 * @example
 * ```typescript
 * await removeOrganizationsFromCampaign('CAMPAIGN_20251028_121851', [123, 456]);
 * console.log('Organizations removed successfully');
 * ```
 */
export async function removeOrganizationsFromCampaign(
  campaignId: string,
  orgIds: number[]
): Promise<SuccessResponse> {
  return apiCall(`remove organizations from campaign ${campaignId}`, () =>
    apiClient.delete<SuccessResponse>(
      `/campaigns/${campaignId}/organizations`,
      { data: { organization_ids: orgIds } }
    )
  );
}

/**
 * Update campaign contact selections (label_contact_selections)
 *
 * This updates which contacts within each organization are selected for labels.
 *
 * @param campaignId - Campaign ID
 * @param selections - Map of org_id -> contact_ids
 * @returns Promise resolving to updated campaign
 *
 * @example
 * ```typescript
 * await updateCampaignSelections('CAMPAIGN_20251028_121851', {
 *   label_contact_selections: {
 *     '123': [456, 789],  // Org 123 has contacts 456, 789 selected
 *     '124': [111, 222, 333]  // Org 124 has contacts 111, 222, 333 selected
 *   }
 * });
 * ```
 */
export async function updateCampaignSelections(
  campaignId: string,
  selections: Record<string, number[]>
): Promise<CampaignResponse> {
  return apiCall(`update campaign selections ${campaignId}`, () =>
    apiClient.put<CampaignResponse>(
      `/campaigns/${campaignId}/selections`,
      { label_contact_selections: selections }
    )
  );
}

/**
 * Fetch contacts for a campaign (includes selection state)
 *
 * @param campaignId - Campaign ID
 * @returns Promise resolving to contacts list
 *
 * @example
 * ```typescript
 * const contacts = await fetchCampaignContacts('CAMPAIGN_20251028_121851');
 * console.log(contacts); // Array of contacts with selection state
 * ```
 */
export async function fetchCampaignContacts(campaignId: string): Promise<ContactItem[]> {
  return apiCall(`fetch campaign contacts ${campaignId}`, () =>
    apiClient.get<ContactItem[]>(`/campaigns/${campaignId}/contacts`)
  );
}

/**
 * Fetch lightweight organization list for a campaign (addresses only)
 *
 * This is a lightweight endpoint optimized for address verification.
 * Returns only org_id, org_name, and address (no full org details).
 *
 * @param campaignId - Campaign ID
 * @returns Promise resolving to lightweight organization list
 *
 * @example
 * ```typescript
 * const result = await fetchCampaignOrganizations('CAMPAIGN_20251028_121851');
 * console.log(result.organizations.length); // Number of organizations
 * ```
 */
export async function fetchCampaignOrganizations(campaignId: string): Promise<{
  campaign_id: string;
  organizations: Array<{
    org_id: number;
    org_name: string;
    address: string;
  }>;
  total: number;
}> {
  return apiCall(`fetch campaign organizations ${campaignId}`, () =>
    apiClient.get<{
      campaign_id: string;
      organizations: Array<{
        org_id: number;
        org_name: string;
        address: string;
      }>;
      total: number;
    }>(`/campaigns/${campaignId}/organizations`)
  );
}

/**
 * Toggle campaign star/favorite status
 *
 * @param campaignId - Campaign ID
 * @param starred - New starred state
 * @returns Promise resolving to updated campaign
 *
 * @example
 * ```typescript
 * await toggleCampaignStar('CAMPAIGN_20251028_121851', true);
 * console.log('Campaign starred');
 * ```
 */
export async function toggleCampaignStar(
  campaignId: string,
  starred: boolean
): Promise<CampaignResponse> {
  return apiCall(`toggle star for campaign ${campaignId}`, () =>
    apiClient.patch<CampaignResponse>(
      `/campaigns/${campaignId}`,
      { starred }
    )
  );
}

/**
 * Add labels to campaign
 *
 * @param campaignId - Campaign ID
 * @param labels - Labels to add
 * @returns Promise resolving to updated campaign
 *
 * @example
 * ```typescript
 * await addLabelsToCampaign('CAMPAIGN_20251028_121851', [
 *   { id: 'label_1', name: 'High Priority', color: '#FF5733' }
 * ]);
 * ```
 */
export async function addLabelsToCampaign(
  campaignId: string,
  labels: CampaignLabel[]
): Promise<CampaignResponse> {
  // Fetch current campaign to merge labels
  const campaign = await fetchCampaignById(campaignId);
  const existingLabels = campaign.labels || [];

  // Merge labels (avoid duplicates by ID)
  const labelMap = new Map<string, CampaignLabel>();
  existingLabels.forEach((label) => labelMap.set(label.id, label));
  labels.forEach((label) => labelMap.set(label.id, label));

  const mergedLabels = Array.from(labelMap.values());

  return apiCall(`add labels to campaign ${campaignId}`, () =>
    apiClient.patch<CampaignResponse>(
      `/campaigns/${campaignId}`,
      { labels: mergedLabels }
    )
  );
}

/**
 * Remove labels from campaign
 *
 * @param campaignId - Campaign ID
 * @param labelIds - Label IDs to remove
 * @returns Promise resolving to updated campaign
 *
 * @example
 * ```typescript
 * await removeLabelsFromCampaign('CAMPAIGN_20251028_121851', ['label_1', 'label_2']);
 * ```
 */
export async function removeLabelsFromCampaign(
  campaignId: string,
  labelIds: string[]
): Promise<CampaignResponse> {
  // Fetch current campaign to filter labels
  const campaign = await fetchCampaignById(campaignId);
  const existingLabels = campaign.labels || [];

  // Filter out removed labels
  const filteredLabels = existingLabels.filter(
    (label) => !labelIds.includes(label.id)
  );

  return apiCall(`remove labels from campaign ${campaignId}`, () =>
    apiClient.patch<CampaignResponse>(
      `/campaigns/${campaignId}`,
      { labels: filteredLabels }
    )
  );
}

/**
 * Add tags to campaign
 *
 * @param campaignId - Campaign ID
 * @param tags - Tags to add
 * @returns Promise resolving to updated campaign
 *
 * @example
 * ```typescript
 * await addTagsToCampaign('CAMPAIGN_20251028_121851', ['Q4', 'Priority']);
 * ```
 */
export async function addTagsToCampaign(
  campaignId: string,
  tags: string[]
): Promise<CampaignResponse> {
  // Fetch current campaign to merge tags
  const campaign = await fetchCampaignById(campaignId);
  const existingTags = campaign.tags || [];

  // Merge tags (unique)
  const mergedTags = Array.from(new Set([...existingTags, ...tags]));

  return apiCall(`add tags to campaign ${campaignId}`, () =>
    apiClient.patch<CampaignResponse>(
      `/campaigns/${campaignId}`,
      { tags: mergedTags }
    )
  );
}

/**
 * Remove tags from campaign
 *
 * @param campaignId - Campaign ID
 * @param tags - Tags to remove
 * @returns Promise resolving to updated campaign
 *
 * @example
 * ```typescript
 * await removeTagsFromCampaign('CAMPAIGN_20251028_121851', ['Q4']);
 * ```
 */
export async function removeTagsFromCampaign(
  campaignId: string,
  tags: string[]
): Promise<CampaignResponse> {
  // Fetch current campaign to filter tags
  const campaign = await fetchCampaignById(campaignId);
  const existingTags = campaign.tags || [];

  // Filter out removed tags
  const filteredTags = existingTags.filter((tag) => !tags.includes(tag));

  return apiCall(`remove tags from campaign ${campaignId}`, () =>
    apiClient.patch<CampaignResponse>(
      `/campaigns/${campaignId}`,
      { tags: filteredTags }
    )
  );
}

/**
 * Bulk update campaigns
 *
 * @param request - Bulk update request with campaign IDs and updates
 * @returns Promise resolving to bulk update response
 *
 * @example
 * ```typescript
 * const result = await bulkUpdateCampaigns({
 *   campaign_ids: ['CAMPAIGN_1', 'CAMPAIGN_2'],
 *   updates: { status: 'archived', starred: false }
 * });
 * console.log(`Updated ${result.updated_count} campaigns`);
 * ```
 */
export async function bulkUpdateCampaigns(
  request: BulkUpdateRequest
): Promise<BulkUpdateResponse> {
  // Note: This endpoint doesn't exist yet in the backend
  // For now, we'll update campaigns one by one
  const promises = request.campaign_ids.map((id) =>
    updateCampaign(id, request.updates).catch((err) => {
      console.error(`Failed to update campaign ${id}:`, err);
      return null;
    })
  );

  const results = await Promise.all(promises);
  const updated = results.filter((r) => r !== null);

  return {
    updated_count: updated.length,
    failed_count: request.campaign_ids.length - updated.length,
    campaign_ids: updated.map((r) => r!.campaign_id),
  };
}

/**
 * Bulk star/unstar campaigns
 *
 * @param campaignIds - Campaign IDs to update
 * @param starred - Star (true) or unstar (false)
 * @returns Promise resolving to bulk update response
 *
 * @example
 * ```typescript
 * const result = await bulkStarCampaigns(
 *   ['CAMPAIGN_1', 'CAMPAIGN_2', 'CAMPAIGN_3'],
 *   true
 * );
 * console.log(`Starred ${result.updated_count} campaigns`);
 * ```
 */
export async function bulkStarCampaigns(
  campaignIds: string[],
  starred: boolean
): Promise<BulkUpdateResponse> {
  return apiCall('bulk star campaigns', () =>
    apiClient.post<BulkUpdateResponse>('/campaigns/bulk-star', {
      campaign_ids: campaignIds,
      starred,
    })
  );
}
